import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Image from '../components/image/Image';
import Table from '../components/table/table';
import Input from '../components/input/Input';
import Button from '../components/button/Button';
import Icon from '../components/icon/Icon';
import Tabs from '../components/tabs/Tabs';
import { ApprovedApplicationsData, ITableData } from '../types/interfaces';
import { hasContent } from '../utils/Utils';
import { getGuaranteesColumns } from '../utils/guaranteesColumns';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import { getApprovedAmounts } from '../services/GuaranteesService';
import FilterModal from '../components/modals/FilterModal';
import { getFilters } from '../services/ApplicationService';
import { FilterData } from '../components/modals/FilterModal/FilterModal.types';
import { GDA_ITEMS_PER_PAGE, type GuaranteesListState } from '../utils/guaranteesListState';
import MonetizedAvalesTab from './MonetizedAvalesTab';
import { useFiscalIdConfig } from '../hooks/useFiscalIdConfig';
import PageBreadcrumb from '../components/PageBreadcrumb/PageBreadcrumb';
import { routes } from '../routes/routePaths';
import { useConfig } from '../context/ConfigContext';
import { resolveLocalCurrencyCode } from '../utils/quotationAssets';

const ApprovedAmountsContent = () => {
  const fiscalId = useFiscalIdConfig();
  const localCurrencyCode = resolveLocalCurrencyCode(useConfig());
  const navigate = useNavigate();
  const location = useLocation();
  const restored = location.state as GuaranteesListState | null;
  const [guarantees, setGuarantees] = useState<ApprovedApplicationsData[]>([]);
  const [tableData, setTableData] = useState<ITableData>({
    page: 0,
    size: GDA_ITEMS_PER_PAGE,
    totalElements: 0,
    totalPages: 0,
  });
  const [page, setPage] = useState<number>(restored?.page ?? 1);
  const [searchText, setSearchText] = useState<string>(restored?.searchText ?? '');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>(
    restored?.activeFilters ?? {}
  );
  const [filterData, setFilterData] = useState<FilterData | null>(null);
  const [isClearFiltersHovered, setIsClearFiltersHovered] =
    useState<boolean>(false);

  const lastRequest = useRef(0);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const response = await getFilters('client');
        if (response.success && 'data' in response) {
          setFilterData(response.data);
        }
      } catch (error) {
        console.error('Error loading filters:', error);
      }
    };
    loadFilters();
  }, []);

  useEffect(() => {
    fetchApprovedAmounts(page, searchText, activeFilters);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchText(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      fetchApprovedAmounts(1, value, activeFilters);
    }, 500);
  };

  const fetchApprovedAmounts = async (
    targetPage: number,
    targetSearch: string,
    filters: Record<string, string>
  ) => {
    setIsLoading(true);
    const requestId = Date.now();
    lastRequest.current = requestId;

    try {
      const response = await getApprovedAmounts(
        targetPage - 1,
        GDA_ITEMS_PER_PAGE,
        targetSearch,
        filters
      );
      if (lastRequest.current !== requestId) return;

      if (hasContent(response)) {
        const totalPages = response.data.total_pages;
        const safePage =
          totalPages > 0 && targetPage > totalPages ? totalPages : targetPage;

        if (safePage !== targetPage) {
          setPage(safePage);
          fetchApprovedAmounts(safePage, targetSearch, filters);
          return;
        }

        setTableData({
          size: response.data.size,
          page: safePage - 1,
          totalElements: response.data.total_elements,
          totalPages,
        });

        setGuarantees(response.data.content || []);
      } else {
        setGuarantees([]);
        setTableData({
          page: 0,
          size: GDA_ITEMS_PER_PAGE,
          totalElements: 0,
          totalPages: 0,
        });
      }
    } catch {
      setGuarantees([]);
      setTableData({
        page: 0,
        size: GDA_ITEMS_PER_PAGE,
        totalElements: 0,
        totalPages: 0,
      });
    }

    setIsLoading(false);
  };

  const handleChangePage = (data: number) => {
    const actualPage = data + 1;
    if (actualPage < 1 || actualPage > tableData.totalPages) {
      return;
    }
    setPage(actualPage);
    setTableData((prev) => ({ ...prev, page: data }));
    fetchApprovedAmounts(actualPage, searchText, activeFilters);
  };

  const handleRowClicked = (row: ApprovedApplicationsData) => {
    navigate(`/avales/${row.cuit}`, {
      state: { page, searchText, activeFilters, companyName: row.company_name },
    });
  };

  const handleOpenModal = () => {
    setIsFilterModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsFilterModalOpen(false);
  };

  const handleApplyFilters = (queryParams: Record<string, string>) => {
    setActiveFilters(queryParams);
    setPage(1);
    fetchApprovedAmounts(1, searchText, queryParams);
  };

  const handleClearFilters = () => {
    setActiveFilters({});
    setPage(1);
    fetchApprovedAmounts(1, searchText, {});
  };

  const getActiveFilterLabels = (): Array<{
    type: string;
    label: string;
    filterKey: string;
  }> => {
    if (!filterData || Object.keys(activeFilters).length === 0) {
      return [];
    }

    const labels: Array<{ type: string; label: string; filterKey: string }> =
      [];

    if (activeFilters.referrerName) {
      const referrer = filterData.referrers.find(
        (r) => r.id.toString() === activeFilters.referrerName
      );
      if (referrer) {
        labels.push({
          type: 'Referido/Vendedor',
          label: referrer.name,
          filterKey: 'referrerName',
        });
      } else {
        labels.push({
          type: 'Referido/Vendedor',
          label: 'Desconocido',
          filterKey: 'referrerName',
        });
      }
    }

    return labels;
  };

  const handleRemoveFilter = (filterKey: string) => {
    const newFilters = { ...activeFilters };
    delete newFilters[filterKey];
    setActiveFilters(newFilters);
    setPage(1);
    fetchApprovedAmounts(1, searchText, newFilters);
  };

  const columns = getGuaranteesColumns(fiscalId.country, localCurrencyCode);

  return (
    <>
      <Card display='flex' gap='16px' alignItems='center' margin={{ top: '4' }}>
        <Button variant='secondary-1' onClick={handleOpenModal}>
          <Card display='flex' alignItems='center' gap='8px'>
            <Icon name='other_filter' variant='secondary-1' width={20} height={20} />
            <P variant='secondary-1' withMargin={false}>Editar filtros</P>
          </Card>
        </Button>
        <Card width='400px' display='flex' gap='16px'>
          <Input
            type='text'
            size='md'
            icons={{ iconLeft: 'search_outline' }}
            placeholder={fiscalId.searchPlaceholder}
            value={searchText}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </Card>
      </Card>
      {Object.keys(activeFilters).length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: '16px',
            alignItems: 'center',
            marginTop: '8px',
            flexWrap: 'wrap',
            backgroundColor: '#F5F7FA',
            padding: '16px 24px',
            borderRadius: '4px',
            border: '1px solid #E5E7EB',
          }}
        >
          {getActiveFilterLabels().map((filter, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {filter.filterKey === 'status' ? (
                <>
                  <P variant='neutral' withMargin={false} type='3' weight='bold'>
                    {filter.type}
                  </P>
                  <div
                    style={{
                      backgroundColor: '#A16108',
                      padding: '4px 12px',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      height: '24px',
                    }}
                  >
                    <P
                      variant='neutral'
                      modifier='inverted'
                      withMargin={false}
                      type='3'
                      weight='bold'
                      style={{ color: '#FFFFFF', fontSize: '11px', lineHeight: '16px', letterSpacing: '0.3px' }}
                    >
                      {filter.label.toUpperCase()}
                    </P>
                  </div>
                </>
              ) : (
                <>
                  <Icon
                    name='other_filter'
                    variant='secondary-1'
                    width={16}
                    height={16}
                  />
                  <P variant='neutral' withMargin={false} type='3'>
                    <span style={{ fontWeight: 'bold' }}>{filter.type}</span>{' '}
                    {filter.label}
                  </P>
                </>
              )}
            </div>
          ))}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
            }}
            onMouseEnter={() => setIsClearFiltersHovered(true)}
            onMouseLeave={() => setIsClearFiltersHovered(false)}
            onClick={handleClearFilters}
          >
            <Icon
              name='delete_outline'
              variant='neutral'
              width={20}
              height={20}
              style={{ color: activeFilters.status ? '#A16108' : '#585053' }}
            />
            <P variant='neutral' withMargin={false} type='3' style={{ color: activeFilters.status ? '#A16108' : '#585053' }}>
              Borrar filtros
            </P>
          </div>
        </div>
      )}

      {isLoading ? (
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={GDA_ITEMS_PER_PAGE} columns={columns.length} />
        </Card>
      ) : guarantees.length > 0 ? (
        <Card margin={{ top: '4' }}>
          <Table
            columns={columns}
            data={guarantees}
            tableData={tableData}
            handleChangePage={handleChangePage}
            onRowClicked={handleRowClicked}
          />
        </Card>
      ) : searchText !== '' ? (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No encontramos resultados.
          </P>
          <P variant='primary' align='center'>
            Podés hacer una nueva búsqueda.
          </P>
        </Card>
      ) : (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No hay clientes con garantías.
          </P>
          <P variant='primary' align='center'>
            Los clientes aparecerán aquí una vez que tengan líneas aprobadas.
          </P>
        </Card>
      )}

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={handleCloseModal}
        onApplyFilters={handleApplyFilters}
        showCommercialFilter={false}
        showProducerFilter={false}
        showReferrerFilter={true}
        showStatusFilter={false}
        clientReferred={true}
        initialFilters={activeFilters}
      />
    </>
  );
};

const GuaranteesManagement = () => {
  return (
    <>
      <div style={{ width: '85vw' }}>
        <PageBreadcrumb
          items={[{ label: 'Inicio', to: routes.dashboard }, { label: 'Gestión de avales' }]}
        />
      </div>
    <Card
      variant='neutral'
      modifier='inverted'
      spacing={'4'}
      height='-webkit-fill-available'
      width='85vw'
    >
      <Card display='flex' justifyContent='space-between'>
        <Card display='flex' alignItems='center'>
          <H withMargin={false} type='1' weight='bold' modifier='strong'>
            Gestión de Avales
          </H>
        </Card>
      </Card>

      <Tabs
        tabs={[
          {
            label: 'Avales por cliente',
            content: <ApprovedAmountsContent />,
          },
          {
            label: 'Avales monetizados',
            content: <MonetizedAvalesTab />,
          },
        ]}
        defaultActiveTab={0}
      />
    </Card>
    </>
  );
};

export default GuaranteesManagement;
